import { Download, Loader2 } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { apiDownload } from '@/lib/api';

/**
 * Export the screen's current result set as CSV.
 *
 * The `body` passed here is the SAME filter object the screen queried with. That
 * is the point: a separate export query is how a spreadsheet starts disagreeing
 * with the page it came from, and the disagreement only surfaces later, inside
 * someone's filing.
 *
 * The server decides truncation, the filename and the caveats. Nothing about
 * what the file contains is settled on this side.
 */
export function ExportButton({
  path,
  body,
  label,
  disabled = false,
}: {
  path: string;
  /** Omit for a GET export. */
  body?: unknown;
  label?: string;
  /**
   * For screens whose unfiltered result set is the whole corpus. The document
   * search is one: with no query it matches 2.46M rows, and an export of those
   * is a truncated ten thousand that reads as an answer.
   */
  disabled?: boolean;
}) {
  const { t } = useTranslation('common');
  const [busy, setBusy] = useState(false);

  const run = async () => {
    setBusy(true);
    try {
      await apiDownload(path, body === undefined ? {} : { method: 'POST', body: JSON.stringify(body) });
    } catch (error) {
      // A failed export must say so. Silently doing nothing reads as a browser
      // that swallowed the download, and the user retries forever.
      toast.error(t('export.failed'), {
        description: error instanceof Error ? error.message : undefined,
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      type="button"
      onClick={() => void run()}
      disabled={busy || disabled}
      className="inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-sm text-foreground disabled:opacity-50"
    >
      {busy ? (
        <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
      ) : (
        <Download className="h-3.5 w-3.5" aria-hidden />
      )}
      {label ?? t('export.csv')}
    </button>
  );
}
